import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Cpu, Plane, Bot, Building2, BrainCircuit, Wheat, Factory, ChevronRight, Globe2, Layers, BadgeCheck } from "lucide-react";
import { COMPANY, INDUSTRIES, SOLUTION_CATEGORIES, EMAILS } from "@/lib/content";
import VerificationPanel from "@/components/VerificationPanel";
import EnquiryForm from "@/components/EnquiryForm";
import EmailStrip from "@/components/EmailStrip";

const ICONS = { Shield: ShieldCheck, Building2, Factory, Wheat, Bot, BrainCircuit, Cpu };

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="vj-radial-fade vj-dark relative overflow-hidden">
        <div className="absolute inset-0 vj-grid-bg opacity-50" />
        <div className="relative px-6 lg:px-12 pt-20 pb-24 max-w-[1400px]">
          <div className="vj-chip-dark" data-testid="hero-tagline-chip">
            <span style={{width:6,height:6,background:'#10B981',borderRadius:'50%'}}></span>
            Made in India · Built for the World
          </div>
          <h1 className="font-display font-black mt-7 text-white leading-[1.02] text-4xl sm:text-5xl lg:text-6xl tracking-tighter max-w-5xl" data-testid="hero-headline">
            Engineering the Future of <span className="text-[#93C5FD]">Drones</span>, Defence,<br className="hidden sm:block"/> Robotics, Automation & Artificial Intelligence.
          </h1>
          <p className="mt-7 text-slate-300 text-base md:text-lg max-w-2xl leading-relaxed">
            VAJRON Global Tech Pvt. Ltd. develops mission-critical aerial systems, autonomous platforms, intelligent monitoring, and engineering solutions for defence, government, and global enterprise customers.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/solutions" className="vj-btn-primary" data-testid="hero-cta-explore">
              Explore Solutions <ArrowRight size={16}/>
            </Link>
            <Link to="/contact" className="vj-btn-ghost-dark" data-testid="hero-cta-contact">
              Contact Us
            </Link>
          </div>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl">
            {[
              { k: "07", v: "Capability Domains" },
              { k: "40+", v: "Solutions & Platforms" },
              { k: "100%", v: "Indian Engineering" },
              { k: "∞", v: "Mission Ambition" },
            ].map((s) => (
              <div key={s.v} className="border-l border-slate-700 pl-4">
                <div className="font-display text-3xl text-white font-extrabold">{s.k}</div>
                <div className="overline text-slate-400 mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="px-6 lg:px-12 py-20 max-w-[1400px]">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="overline">01 / Company</div>
            <h2 className="font-display text-3xl lg:text-4xl font-extrabold mt-3 tracking-tight">An Indian technology enterprise with global ambition.</h2>
          </div>
          <div className="lg:col-span-8 text-slate-600 text-[15px] leading-relaxed space-y-4">
            <p>
              VAJRON Global Tech Pvt. Ltd. is an Indian technology company engineering advanced drone systems, robotics,
              automation, artificial intelligence, intelligent sensing platforms, embedded electronics, and mission-critical
              software for defence organizations, government agencies, industries, and research institutions.
            </p>
            <p>
              Our work is engineered for scale, configurability, and operational seriousness — designed to support nations and
              enterprises in surveillance, monitoring, automation, industrial operations, infrastructure intelligence, and
              autonomous decision-making.
            </p>
            <div className="vj-divider my-6"/>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
              {INDUSTRIES.slice(0, 8).map((i) => (
                <div key={i} className="text-sm text-[#0F172A] font-medium" data-testid={`industry-${i.toLowerCase().replace(/\s+/g,'-')}`}>
                  <ChevronRight size={12} className="inline text-[#1E3A8A]"/> {i}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="px-6 lg:px-12 py-16 bg-slate-50 border-y border-slate-200">
        <div className="max-w-[1400px]">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <div className="overline">02 / Capabilities</div>
              <h2 className="font-display text-3xl lg:text-4xl font-extrabold mt-3 tracking-tight">Seven engineering domains.<br/>One integrated technology house.</h2>
            </div>
            <Link to="/solutions" className="vj-link inline-flex items-center gap-1" data-testid="capabilities-view-all">
              View full Solutions matrix <ArrowRight size={14}/>
            </Link>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SOLUTION_CATEGORIES.map((cat) => {
              const Icon = ICONS[cat.icon] || Layers;
              return (
                <Link to="/solutions" key={cat.id} className="vj-card p-6 block" data-testid={`capability-card-${cat.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="h-10 w-10 border border-slate-200 flex items-center justify-center text-[#1E3A8A]">
                      <Icon size={18} strokeWidth={1.6}/>
                    </div>
                    <span className="overline">{cat.overline}</span>
                  </div>
                  <h3 className="font-display font-bold text-lg mt-5 leading-snug">{cat.title}</h3>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{cat.description}</p>
                  <div className="mt-5 inline-flex items-center text-[#1E3A8A] text-sm font-semibold">
                    Explore <ArrowRight size={13} className="ml-1"/>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* VERIFICATION CTA */}
      <section className="px-6 lg:px-12 py-20">
        <div className="max-w-[1400px] grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="overline">03 / Verification</div>
            <h2 className="font-display text-3xl lg:text-4xl font-extrabold mt-3 tracking-tight">Public Credential Verification</h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Verify the authenticity of any VAJRON-issued internship credential. Enter the Credential ID to retrieve the holder's
              name, institute, role, and live status — permanently verifiable.
            </p>
            <div className="mt-5 inline-flex items-center gap-2 text-sm text-emerald-700 font-mono">
              <BadgeCheck size={14}/> PERMANENTLY VERIFIED · LIVE STATUS SYNC
            </div>
          </div>
          <div className="lg:col-span-8">
            <VerificationPanel />
          </div>
        </div>
      </section>

      {/* ENQUIRY */}
      <section id="enquiry-section" className="px-6 lg:px-12 py-20 bg-slate-50 border-y border-slate-200" data-testid="home-enquiry-section">
        <div className="max-w-[1400px] grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="overline">05 / Enquiries</div>
            <h2 className="font-display text-3xl lg:text-4xl font-extrabold mt-3 tracking-tight">Have a requirement? Send us an enquiry.</h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Enquire about any of our drone, robotics, AI, or automation technologies — or send a custom enquiry for a
              solution tailored to your mission. Our engineering team responds to every request.
            </p>
            <Link to="/solutions" className="vj-link inline-flex items-center gap-1 mt-5" data-testid="home-enquiry-browse">
              Browse all technologies <ArrowRight size={14}/>
            </Link>
          </div>
          <div className="lg:col-span-8">
            <EnquiryForm />
          </div>
        </div>
      </section>

      {/* CAREERS CTA */}
      <section className="px-6 lg:px-12 py-16 vj-dark">
        <div className="max-w-[1400px] grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="overline text-slate-400">04 / Careers</div>
            <h2 className="font-display text-3xl lg:text-4xl font-extrabold text-white mt-3 tracking-tight">Build technology that defines the next era of Indian engineering.</h2>
            <p className="text-slate-400 mt-4 leading-relaxed max-w-2xl">
              We hire engineers, researchers, and interns committed to engineering excellence across drones, robotics, autonomous systems, and intelligent software.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-wrap gap-3">
            <Link to="/careers" className="vj-btn-primary" data-testid="home-careers-link">
              View Open Roles <ArrowRight size={16}/>
            </Link>
            <Link to="/careers" className="vj-btn-ghost-dark" data-testid="home-internship-link">
              Internship Programs
            </Link>
          </div>
        </div>
      </section>

      <EmailStrip
        heading="For general & business enquiries, reach our team directly."
        emails={[{ label: "General Enquiries", address: EMAILS.general }]}
      />
    </div>
  );
}
